import {useState, useEffect} from 'react'
import axios from 'axios'
import {Modal, Button} from '@origamirisk/origami-storybook'
import {Menu, MenuItem} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {formatEffectiveDate, formatFullTimestamp} from '../../utils/dateFormatter'
import {Version} from './types'
import VersionFileViewer from './VersionFileViewer'
import {useAccount} from '../../context/AccountContext'
import ConfirmationModal from '../common/ConfirmationModal'

interface VersionDetailsModalProps {
    isOpen: boolean
    version: Version
    onClose: () => void
    onVersionUpdated?: () => void
}

function VersionDetailsModal({isOpen, version, onClose, onVersionUpdated}: VersionDetailsModalProps) {
    const {selectedAccount} = useAccount()
    const [diffMode, setDiffMode] = useState(false)
    const [compareVersion, setCompareVersion] = useState<Version | null>(null)
    const [availableVersions, setAvailableVersions] = useState<Version[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false)
    const [selectedCompareState, setSelectedCompareState] = useState<string>('')
    const [previousVersion, setPreviousVersion] = useState<Version | null>(null)
    const [loadingPreviousVersion, setLoadingPreviousVersion] = useState(false)
    const [hasPreselectedPreviousVersion, setHasPreselectedPreviousVersion] = useState(false)
    const [showMetadata, setShowMetadata] = useState(false)
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
    const [confirmModalOpen, setConfirmModalOpen] = useState(false)
    const [confirmModalConfig, setConfirmModalConfig] = useState<{
        title: string
        message: string
        confirmText: string
        confirmColor: 'primary' | 'secondary' | 'danger'
        onConfirm: () => void
    }>({
        title: '',
        message: '',
        confirmText: 'Confirm',
        confirmColor: 'primary',
        onConfirm: () => {}
    })

    const handleVersionUpdated = () => {
        // Don't close modal, just refresh
        onVersionUpdated?.()
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }

    const handleDeleteVersion = () => {
        handleMenuClose()
        setConfirmModalConfig({
            title: 'Delete Version',
            message: `Are you sure you want to delete version ${version.label || version.versionId}? This will mark it as DELETED and prevent it from appearing in lists.`,
            confirmText: 'Delete',
            confirmColor: 'danger',
            onConfirm: async () => {
                setConfirmModalOpen(false)
                try {
                    await axios.delete(`/api/admin/versions/${version.versionId}`)
                    handleVersionUpdated()
                } catch (error) {
                    console.error('Failed to delete version:', error)
                    alert('Failed to delete version')
                }
            }
        })
        setConfirmModalOpen(true)
    }

    const handleUndeleteVersion = () => {
        setConfirmModalConfig({
            title: 'Restore Version',
            message: `Are you sure you want to restore version ${version.label || version.versionId}? This will mark it as PUBLISHED and make it visible again.`,
            confirmText: 'Restore',
            confirmColor: 'secondary',
            onConfirm: async () => {
                setConfirmModalOpen(false)
                try {
                    await axios.patch(`/api/admin/versions/${version.versionId}/undelete`)
                    handleVersionUpdated()
                } catch (error) {
                    console.error('Failed to restore version:', error)
                    alert('Failed to restore version')
                }
            }
        })
        setConfirmModalOpen(true)
    }

    // Fetch previous version automatically for summary view
    useEffect(() => {
        const fetchPreviousVersion = async () => {
            if (!selectedAccount) {
                setPreviousVersion(null)
                setLoadingPreviousVersion(false)
                return
            }

            setLoadingPreviousVersion(true)
            try {
                const {data} = await axios.post<Version[]>('/api/admin/versions', {
                    includeDeleted: true
                })

                // Filter versions with same accountConnectionId, clientId, policySetId, carrierId, and state
                // Exclude deleted versions from previous version comparison
                const matchingVersions = data.filter(v =>
                    v.versionId !== version.versionId &&
                    v.accountConnectionId === version.accountConnectionId &&
                    v.clientId === version.clientId &&
                    v.policySetId === version.policySetId &&
                    v.carrierId === version.carrierId &&
                    v.state === version.state &&
                    v.status !== 'DELETED'
                )

                // Helper to compare version labels (v1.44 > v1.43.1 > v1.43 > v1.42)
                const parseVersion = (label: string) => {
                    const match = label.match(/v?(\d+)\.(\d+)(?:\.(\d+))?/)
                    if (!match) return null
                    return {
                        major: parseInt(match[1]),
                        minor: parseInt(match[2]),
                        patch: match[3] ? parseInt(match[3]) : 0,
                        raw: label
                    }
                }

                const currentVersionParsed = parseVersion(version.label || version.versionId)
                if (!currentVersionParsed) {
                    setPreviousVersion(null)
                    return
                }

                // Sort by version number descending
                const sorted = matchingVersions
                    .map(v => ({version: v, parsed: parseVersion(v.label || v.versionId)}))
                    .filter(v => v.parsed !== null)
                    .sort((a, b) => {
                        if (a.parsed!.major !== b.parsed!.major) return b.parsed!.major - a.parsed!.major
                        if (a.parsed!.minor !== b.parsed!.minor) return b.parsed!.minor - a.parsed!.minor
                        return b.parsed!.patch - a.parsed!.patch
                    })

                // Find the first version that's lower than the current version
                const prev = sorted.find(v => {
                    const p = v.parsed!
                    const c = currentVersionParsed
                    if (p.major < c.major) return true
                    if (p.major === c.major && p.minor < c.minor) return true
                    if (p.major === c.major && p.minor === c.minor && p.patch < c.patch) return true
                    return false
                })

                setPreviousVersion(prev?.version || null)
            } catch (e) {
                console.error('Failed to fetch previous version:', e)
                setPreviousVersion(null)
            } finally {
                setLoadingPreviousVersion(false)
            }
        }

        fetchPreviousVersion()
    }, [version, selectedAccount])

    useEffect(() => {
        if (!diffMode) {
            setCompareVersion(null)
            setSelectedCompareState('')
            setHasPreselectedPreviousVersion(false)
            return
        }

        const fetchVersions = async () => {
            if (!selectedAccount) {
                setAvailableVersions([])
                return
            }

            // Only fetch if we don't already have versions loaded
            if (availableVersions.length === 0) {
                setLoadingVersions(true)
                try {
                    const {data} = await axios.post<Version[]>('/api/admin/versions', {
                        accountName: selectedAccount,
                        includeDeleted: true
                    })
                    // Filter out the current version
                    const filtered = data.filter(v => v.versionId !== version.versionId)
                    setAvailableVersions(filtered)
                } catch (e) {
                    console.error('Failed to fetch versions:', e)
                    setAvailableVersions([])
                } finally {
                    setLoadingVersions(false)
                }
            }
        }

        fetchVersions()
    }, [diffMode, version.versionId, selectedAccount])

    // Helper function to compare version numbers
    const compareVersionNumbers = (versionA: string, versionB: string) => {
        const partsA = versionA.split('.').map(Number)
        const partsB = versionB.split('.').map(Number)
        const maxLength = Math.max(partsA.length, partsB.length)

        for (let i = 0; i < maxLength; i++) {
            const a = partsA[i] || 0
            const b = partsB[i] || 0
            if (a !== b) {
                return b - a // Descending order (latest first)
            }
        }
        return 0
    }

    // Pre-select previous version when entering diff mode for the first time
    useEffect(() => {
        if (diffMode && previousVersion && !hasPreselectedPreviousVersion) {
            setSelectedCompareState(previousVersion.state)
            setCompareVersion(previousVersion)
            setHasPreselectedPreviousVersion(true)
        }
    }, [diffMode, previousVersion, hasPreselectedPreviousVersion])

    // Get unique states
    const availableStates = [...new Set(availableVersions.map(v => v.state))].sort()

    // Filter versions by selected state and sort by version number
    const versionsForSelectedState = selectedCompareState
        ? availableVersions
            .filter(v => v.state === selectedCompareState)
            .sort((a, b) => compareVersionNumbers(a.label || a.versionId, b.label || b.versionId))
        : []
    const titleContent = (
        <div>
            <div style={{fontSize: '20px', fontWeight: 600, marginBottom: '0px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{padding: '5px 10px', borderRadius: '3px', backgroundColor: '#b2ebf2'}}>{version.state}</span>
                <span style={{padding: '5px 10px', borderRadius: '3px', backgroundColor: '#e8eaf6'}}>{version.label || version.versionId}</span>
                <span>Effective Date: {formatEffectiveDate(version.effectiveDate)}</span>
                <span>Published: {formatFullTimestamp(version.publishTimestamp)}</span>

                {/* Delete menu or Restore button */}
                {version.status === 'DELETED' ? (
                    <Button
                        variant="contained"
                        color="primary"
                        size="sm"
                        onClick={handleUndeleteVersion}
                        style={{backgroundColor: '#43a047', color: 'white'}}
                    >
                        Restore Version
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={handleMenuOpen}
                            color="secondary"
                            variant="contained"
                            size="sm"
                            // style={{width: '20px'}}
                        >
                            <MoreVertIcon />
                        </Button>
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleDeleteVersion}>Delete Version</MenuItem>
                        </Menu>
                    </>
                )}
            </div>
            <div style={{marginTop: '12px'}}>
                <div style={{fontSize: '12px', color: '#666'}}>
                    <span>{version.clientName} • {version.policySetName} • {version.carrierName}</span>
                    <a 
                        onClick={() => setShowMetadata(!showMetadata)}
                        style={{marginLeft: '8px', fontSize: '11px', cursor: 'pointer'}}
                    >
                        {showMetadata ? 'less...' : 'more...'}
                    </a>
                </div>
                <div style={{
                    maxHeight: showMetadata ? '200px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease-in-out',
                    marginTop: showMetadata ? '12px' : '0'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '5px',
                        fontSize: '12px'
                    }}>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Client</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{version.clientName}</div>
                            <div style={{color: '#666', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace'}}>ID: {version.clientId}</div>
                        </div>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Rating Program</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{version.policySetName}</div>
                            <div style={{color: '#666', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace'}}>ID: {version.policySetId}</div>
                        </div>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Carrier</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{version.carrierName}</div>
                            <div style={{color: '#666', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace'}}>ID: {version.carrierId}</div>
                        </div>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Version ID</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{version.versionId}</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>hash: {version.versionKeyHash}</div>
                        </div>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Account Connection ID</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{version.accountConnectionId}</div>
                        </div>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#FAFAFA',
                            border: '1px solid #ECECEC',
                            borderRadius: '4px'
                        }}>
                            <div style={{color: '#999', fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Created</div>
                            <div style={{color: '#333', fontSize: '10px', marginTop: '5px', fontFamily: 'monospace', fontWeight: 500}}>{formatFullTimestamp(version.createdTs)}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size="xl"
            title={titleContent as any}
            sx={{
                '& .MuiDialog-paper': {
                    height: '95vh',
                    maxHeight: '95vh',
                    maxWidth: '95vw',
                    width: '95vw'
                }
            }}
        >
            <div style={{
                height: 'calc(100vh - 180px)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{flex: 1, overflow: 'hidden'}}>
                    <VersionFileViewer
                        version={version}
                        compareVersion={diffMode ? compareVersion : undefined}
                        diffMode={diffMode}
                        setDiffMode={setDiffMode}
                        loadingVersions={loadingVersions}
                        availableStates={availableStates}
                        selectedCompareState={selectedCompareState}
                        setSelectedCompareState={setSelectedCompareState}
                        versionsForSelectedState={versionsForSelectedState}
                        setCompareVersion={setCompareVersion}
                        previousVersion={previousVersion}
                        loadingPreviousVersion={loadingPreviousVersion}
                    />
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                open={confirmModalOpen}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                confirmText={confirmModalConfig.confirmText}
                confirmColor={confirmModalConfig.confirmColor}
                onConfirm={confirmModalConfig.onConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
        </Modal>
    )
}

export default VersionDetailsModal
